import React, { useState } from 'react';
import { Alert, Button, Form, InputGroup, Modal } from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';

import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import { Api } from 'src/models/Api.model';

import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';
import useValidatePassword from 'src/hooks/useValidatePassword';

import CustomToast from './CustomToast';

export type ChangePwdModalProps = {
  isShowChangePwdModal: boolean;
  handleCloseChangePwdModal: () => void;
  canBeCanceled?: boolean;
};

export default function ChangePwdModal({
  isShowChangePwdModal,
  handleCloseChangePwdModal,
  canBeCanceled,
}: ChangePwdModalProps) {
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({
    defaultValues: {
      currentPassword: '',
      password: '',
      confirmPassword: '',
    },
    criteriaMode: 'all',
  });

  const { tPages, tCommon } = useUiTranslation();

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const [isShowToast, setIsShowToast] = useState(false);
  const [toastText, setToastText] = useState('');

  const handleShowToast = (text: string) => {
    setToastText(text);
    setIsShowToast(true);
  };

  const { currentUser } = useRemoteBoundStore();

  const userId = currentUser?.userId;

  const closeModalAndReset = () => {
    handleCloseChangePwdModal();
    reset();
  };

  const onSubmit: SubmitHandler<{
    currentPassword: string;
    password: string;
    confirmPassword: string;
  }> = async (data) => {
    try {
      const { currentPassword, password } = data;

      if (!userId) {
        throw new Error(tCommon('message.error.userIdNotFound'));
      }

      await Api.remote.change_password(userId, currentPassword, password);

      closeModalAndReset();
      handleShowToast(tPages('changePassword.passwordChanged'));
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      handleShowToast(errorMessage);
    }
  };

  const [isShowCurrentPassword, setIsShowCurrentPassword] = useState(false);
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false);

  const validatePassword = useValidatePassword();

  return (
    <>
      <CustomModal
        show={isShowChangePwdModal}
        keyboard={canBeCanceled}
        onHide={closeModalAndReset}
      >
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Header closeButton={canBeCanceled}>
            <Modal.Title>{tPages('changePassword.title')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!canBeCanceled && (
              <Alert variant="danger">
                {tPages('changePassword.passwordTooWeak')}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>
                {tPages('changePassword.currentPassword')}
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type={isShowCurrentPassword ? 'text' : 'password'}
                  placeholder={tPages(
                    'changePassword.currentPasswordPlaceholder',
                  )}
                  {...register('currentPassword', {
                    required: tPages('changePassword.currentPasswordRequired'),
                  })}
                />
                <InputGroup.Text
                  onClick={() =>
                    setIsShowCurrentPassword(!isShowCurrentPassword)
                  }
                  className="pointer"
                >
                  <FontAwesomeIcon
                    icon={isShowCurrentPassword ? faEye : faEyeSlash}
                  />
                </InputGroup.Text>
              </InputGroup>
              {errors.currentPassword?.message && (
                <p className="text-danger">{errors.currentPassword?.message}</p>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{tPages('changePassword.newPassword')}</Form.Label>
              <InputGroup>
                <Form.Control
                  type={isShowPassword ? 'text' : 'password'}
                  placeholder={tPages('changePassword.newPasswordPlaceholder')}
                  {...register('password', {
                    required: tPages('changePassword.passwordRequired'),
                    validate: validatePassword,
                  })}
                />
                <InputGroup.Text
                  onClick={() => setIsShowPassword(!isShowPassword)}
                  className="pointer"
                >
                  <FontAwesomeIcon icon={isShowPassword ? faEye : faEyeSlash} />
                </InputGroup.Text>
              </InputGroup>
              <div className="text-danger">
                {errors.password?.type === 'required' ? (
                  <p>{tPages('changePassword.newPasswordRequired')}</p>
                ) : (
                  <ul className="list-unstyled">
                    {errors.password?.types?.minLength && (
                      <li>{errors.password.message}</li>
                    )}
                    {errors.password?.types?.hasUpperCase && (
                      <li>{errors.password.types.hasUpperCase}</li>
                    )}
                    {errors.password?.types?.hasLowerCase && (
                      <li>{errors.password.types.hasLowerCase}</li>
                    )}
                    {errors.password?.types?.hasNumber && (
                      <li>{errors.password.types.hasNumber}</li>
                    )}
                    {errors.password?.types?.hasSpecialChar && (
                      <li>{errors.password.types.hasSpecialChar}</li>
                    )}
                  </ul>
                )}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {tPages('changePassword.confirmPassword')}
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type={isShowConfirmPassword ? 'text' : 'password'}
                  placeholder={tPages(
                    'changePassword.confirmPasswordPlaceholder',
                  )}
                  {...register('confirmPassword', {
                    required: tPages('changePassword.confirmPasswordRequired'),
                    validate: (val: string) => {
                      if (watch('password') !== val) {
                        return tPages('changePassword.passwordsDoNotMatch');
                      }
                    },
                  })}
                />
                <InputGroup.Text
                  onClick={() =>
                    setIsShowConfirmPassword(!isShowConfirmPassword)
                  }
                  className="pointer"
                >
                  <FontAwesomeIcon
                    icon={isShowConfirmPassword ? faEye : faEyeSlash}
                  />
                </InputGroup.Text>
              </InputGroup>
              {
                <p className="text-danger ">
                  {errors.confirmPassword?.message}
                </p>
              }
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            {canBeCanceled && (
              <Button variant="outline-primary" onClick={closeModalAndReset}>
                {tPages('changePassword.cancel')}
              </Button>
            )}
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {tPages('changePassword.change')}
            </Button>
          </Modal.Footer>
        </Form>
      </CustomModal>

      <CustomToast
        children={toastText}
        isShow={isShowToast}
        handleToggleShowToast={(isShow) => setIsShowToast(isShow)}
      />
    </>
  );
}
